import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  IconButton,
  Button,
  Stack,
  alpha,
  Chip,
  Fade,
  Tooltip,
  Zoom,
  Badge,
} from "@mui/material";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import addMonths from "date-fns/addMonths";
import subMonths from "date-fns/subMonths";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  Event,
  ChevronLeft,
  ChevronRight,
  Today,
  CalendarToday,
  Schedule,
  LocationOn,
  People,
  ArrowForward,
  CheckCircle,
  Pending,
} from "@mui/icons-material";

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export default function CalendarView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const meetingsRef = collection(db, "meetings");
        const q = query(meetingsRef, orderBy("date"));
        const querySnapshot = await getDocs(q);

        const meetingsData = [];
        querySnapshot.forEach((doc) => {
          const meeting = doc.data();
          const startDate = new Date(`${meeting.date}T${meeting.startTime}`);
          const endDate = new Date(`${meeting.date}T${meeting.endTime}`);

          meetingsData.push({
            id: doc.id,
            title: meeting.name,
            start: startDate,
            end: endDate,
            allDay: false,
            location: meeting.location,
            participants: meeting.participants || 0,
            notes: meeting.notes || "",
            day: meeting.day || "",
          });
        });

        setMeetings(meetingsData);
      } catch (err) {
        setError("Gagal memuat daftar rapat");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchMeetings();
  }, []);

  const eventStyleGetter = (event) => {
    const now = new Date();
    const isPast = event.end < now;
    const isToday = event.start.toDateString() === now.toDateString();

    let backgroundColor = "#2e7d32"; // Default green
    let opacity = 0.85;

    if (isPast) {
      backgroundColor = "#9e9e9e";
      opacity = 0.6;
    } else if (isToday) {
      backgroundColor = "#ed6c02";
      opacity = 0.9;
    }

    const style = {
      backgroundColor,
      borderRadius: "6px",
      opacity,
      color: "white",
      border: "0px",
      display: "block",
      fontWeight: 500,
      fontSize: isMobile ? "0.7rem" : "0.8rem",
      padding: isMobile ? "2px 4px" : "4px 8px",
      transition: "all 0.2s ease",
      cursor: "pointer",
      boxShadow: `0 2px 8px ${alpha(backgroundColor, 0.3)}`,
      "&:hover": {
        transform: "scale(1.02)",
        boxShadow: `0 4px 12px ${alpha(backgroundColor, 0.4)}`,
      },
    };
    return {
      style,
    };
  };

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleSelectEvent = (event) => {
    navigate(`/report/${event.id}`);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get meetings count for current month
  const getMeetingsCount = () => {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    return meetings.filter((m) => {
      const d = new Date(m.start);
      return d.getMonth() === month && d.getFullYear() === year;
    }).length;
  };

  // Get upcoming meetings
  const getUpcomingMeetings = () => {
    const now = new Date();
    return meetings.filter((m) => m.start > now).slice(0, 3);
  };

  const upcomingMeetings = getUpcomingMeetings();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          mt: 4,
          mx: "auto",
          maxWidth: 800,
          borderRadius: 3,
        }}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 4,
            bgcolor: "background.paper",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          {/* Header */}
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={isMobile ? 2 : 0}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, #1b5e20 0%, #2e7d32 40%, #4caf50 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Kalendar Agenda Rapat
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {meetings.length} total rapat • {getMeetingsCount()} rapat bulan
                ini
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Tooltip title="Kembali ke hari ini" arrow>
                <Button
                  variant="contained"
                  startIcon={<Today />}
                  onClick={goToToday}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: "#2e7d32",
                    boxShadow: `0 4px 12px ${alpha("#2e7d32", 0.3)}`,
                    "&:hover": {
                      bgcolor: "#1b5e20",
                      transform: "translateY(-2px)",
                      boxShadow: `0 6px 20px ${alpha("#2e7d32", 0.4)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Hari Ini
                </Button>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Quick Stats - Upcoming Meetings */}
          {upcomingMeetings.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.info.main, 0.04),
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
              }}
            >
              <Stack
                direction={isMobile ? "column" : "row"}
                spacing={isMobile ? 1 : 2}
                alignItems={isMobile ? "flex-start" : "center"}
              >
                <Chip
                  label="Rapat Mendatang"
                  icon={<Pending />}
                  color="info"
                  size="small"
                  sx={{ fontWeight: 600, borderRadius: 2 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {upcomingMeetings.length} rapat akan datang
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 1, flexWrap: "wrap", flex: 1 }}
                >
                  {upcomingMeetings.map((meeting) => (
                    <Chip
                      key={meeting.id}
                      label={meeting.title}
                      size="small"
                      onClick={() => navigate(`/report/${meeting.id}`)}
                      sx={{
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        "&:hover": {
                          bgcolor: alpha("#2e7d32", 0.08),
                        },
                        cursor: "pointer",
                      }}
                    />
                  ))}
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Calendar */}
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={goToPreviousMonth}
              sx={{
                position: "absolute",
                left: { xs: -8, sm: -12 },
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                bgcolor: "background.paper",
                boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.12)}`,
                "&:hover": {
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) scale(1.05)",
                  boxShadow: `0 6px 24px ${alpha(theme.palette.common.black, 0.18)}`,
                },
                transition: "all 0.3s ease",
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                "& .MuiSvgIcon-root": {
                  fontSize: { xs: 20, sm: 28 },
                },
              }}
            >
              <ChevronLeft />
            </IconButton>

            <Box
              sx={{
                height: isMobile ? 500 : isTablet ? 550 : 650,
                mx: { xs: 0, sm: 3, md: 4 },
                "& .rbc-calendar": {
                  bgcolor: "background.paper",
                  borderRadius: 3,
                },
                "& .rbc-header": {
                  py: 1.5,
                  fontWeight: 600,
                  color: "text.secondary",
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                },
                "& .rbc-row": {
                  border: "none",
                },
                "& .rbc-date-cell": {
                  py: 1,
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                  fontWeight: 500,
                  "& .rbc-button-link": {
                    fontWeight: 500,
                    color: "text.primary",
                    "&:hover": {
                      color: "#2e7d32",
                    },
                  },
                },
                "& .rbc-day-bg": {
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    bgcolor: alpha("#2e7d32", 0.04),
                  },
                },
                "& .rbc-today": {
                  bgcolor: alpha("#2e7d32", 0.06),
                  "& .rbc-date-cell .rbc-button-link": {
                    color: "#2e7d32",
                    fontWeight: 700,
                  },
                },
                "& .rbc-off-range-bg": {
                  bgcolor: alpha(theme.palette.grey[100], 0.3),
                },
                "& .rbc-event": {
                  borderRadius: 6,
                  "&:hover": {
                    opacity: 1,
                  },
                },
                "& .rbc-month-row": {
                  minHeight: isMobile ? 60 : 80,
                },
                "& .rbc-event-content": {
                  fontSize: isMobile ? "0.6rem" : "0.75rem",
                },
              }}
            >
              <Calendar
                localizer={localizer}
                events={meetings}
                startAccessor="start"
                endAccessor="end"
                style={{ height: "100%" }}
                eventPropGetter={eventStyleGetter}
                onSelectEvent={handleSelectEvent}
                onNavigate={handleNavigate}
                date={currentDate}
                views={["month", "agenda"]}
                defaultView="month"
                components={{
                  toolbar: () => null,
                  event: ({ event }) => (
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {event.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            <LocationOn
                              sx={{
                                fontSize: 14,
                                mr: 0.5,
                                verticalAlign: "middle",
                              }}
                            />
                            {event.location}
                          </Typography>
                          {event.participants > 0 && (
                            <Typography variant="caption" display="block">
                              <People
                                sx={{
                                  fontSize: 14,
                                  mr: 0.5,
                                  verticalAlign: "middle",
                                }}
                              />
                              {event.participants} peserta
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          height: "100%",
                          px: { xs: 0.5, sm: 1 },
                          py: 0.5,
                        }}
                      >
                        <Event sx={{ fontSize: isMobile ? 12 : 14 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: isMobile ? "0.55rem" : "0.7rem",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {event.title}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ),
                }}
                messages={{
                  today: "Hari Ini",
                  previous: "Sebelumnya",
                  next: "Selanjutnya",
                  month: "Bulan",
                  agenda: "Agenda",
                  date: "Tanggal",
                  time: "Waktu",
                  event: "Rapat",
                }}
                popup
                popupOffset={{ x: 10, y: 20 }}
              />
            </Box>

            <IconButton
              onClick={goToNextMonth}
              sx={{
                position: "absolute",
                right: { xs: -8, sm: -12 },
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                bgcolor: "background.paper",
                boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.12)}`,
                "&:hover": {
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) scale(1.05)",
                  boxShadow: `0 6px 24px ${alpha(theme.palette.common.black, 0.18)}`,
                },
                transition: "all 0.3s ease",
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                "& .MuiSvgIcon-root": {
                  fontSize: { xs: 20, sm: 28 },
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          {/* Legend and Navigation Info */}
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"}
            sx={{
              mt: 3,
              pt: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}
          >
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 2,
                    bgcolor: "#2e7d32",
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Akan Datang
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 2,
                    bgcolor: "#ed6c02",
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Hari Ini
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 2,
                    bgcolor: "#9e9e9e",
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Selesai
                </Typography>
              </Stack>

              <Chip
                icon={<CalendarToday sx={{ fontSize: 14 }} />}
                label={`${format(currentDate, "MMMM yyyy")}`}
                size="small"
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  color: theme.palette.primary.main,
                }}
              />
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: isMobile ? 2 : 0 }}
            >
              {meetings.length} total rapat • {getMeetingsCount()} bulan ini
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Fade>
  );
}
